import { colors } from '@packages/core/styles';
import { Icon, Typography } from '@packages/ui/shared';
import { ConfirmModal } from '@web/components/confirm-modal';
import React, { useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';

const useStyles = createUseStyles({
    container: {
        border: `2px solid ${colors.grayTwo}`,
        borderRadius: 5,
        backgroundColor: colors.white,
        marginBottom: 24,
    },
    // Applied to Modal via dialogClassName below
    confirmDeleteModal: {
        width: 558,
        maxWidth: 'none',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: colors.grayOne,
        borderBottom: `1px solid ${colors.grayThree}`,
        padding: '12px 16px',
        height: '56px',
    },
    body: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        height: '72px',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
    },
    spacer: {
        flex: 0.1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    primary: {
        fontWeight: 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
});

const styles = StyleSheet.create({
    title: {
        fontSize: 15,
        fontWeight: 600,
        color: colors.textDarkPrimary,
    },
    subTitle: {
        fontSize: 15,
        lineHeight: 1.6,
        fontWeight: 400,
        color: colors.textDarkPrimary,
    },
    description: {
        fontSize: 13,
        fontWeight: 400,
        color: colors.textDarkPrimary,
    },
});

/**
 * Props for the {@link FailCodeCard} component.
 */
export type FailCodeCardProps = {
    /** Additional CSS class names for the card container. */
    className?: string;

    /** The fail code string (e.g. "A1234"). */
    code?: string;

    /** The fail code description text. */
    description?: string;

    /** Header label for the left column. */
    leftTitle?: string;

    /** Callback invoked when the user confirms deletion of this fail code. */
    onDelete?: () => void;

    /** Header label for the right column. */
    rightTitle?: string;

    /** The section name associated with this fail code. */
    section?: string;

    /** Whether to show the delete icon. */
    showDelete?: boolean;

    /** The subsection name associated with this fail code. */
    subsection?: string;

    /** Whether the delete icon should be disabled. */
    disableDelete?: boolean;
};

/**
 * Displays a single fail code as a card with two columns: fail code and description on
 * the left, section and subsection on the right. Optionally shows a delete icon that
 * triggers a confirmation modal before invoking the delete callback.
 *
 * @param props - {@link FailCodeCardProps}
 * @returns the fail code card JSX
 */
export function FailCodeCard({
    leftTitle = 'Fail code & description',
    rightTitle = 'Section & subsection',
    code,
    description,
    section,
    subsection,
    onDelete,
    showDelete,
    className,
    disableDelete = false,
}: FailCodeCardProps) {
    const classes = useStyles();
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);

    // Handlers

    /** Handle the trash icon press by showing the delete confirmation modal. */
    const handleDeleteIconPress = useCallback(() => {
        setShowConfirmDelete(true);
    }, []);

    /** Handle cancelling the delete confirmation modal. */
    const handleDeleteCancelButtonClick = useCallback(() => {
        setShowConfirmDelete(false);
    }, []);

    /** Handle confirming the deletion. Hides the modal and invokes the parent's onDelete callback. */
    const handleDeleteConfirmButtonClick = useCallback(() => {
        setShowConfirmDelete(false);
        onDelete?.();
    }, [onDelete]);

    return (
        <div className={`${classes.container} ${className || ''}`.trim()}>
            <div className={classes.header}>
                <div className={classes.column}>
                    <Typography style={styles.title} variant="h6">
                        {leftTitle}
                    </Typography>
                </div>
                <div className={classes.column}>
                    <Typography style={styles.title} variant="h6">
                        {rightTitle}
                    </Typography>
                </div>
                <div className={classes.spacer} />
            </div>

            <div className={classes.body}>
                <div className={classes.column}>
                    {code && (
                        <div className={classes.primary}>
                            <Typography style={styles.subTitle} variant="h6">
                                {code}
                            </Typography>
                        </div>
                    )}
                    {description && (
                        <Typography style={styles.description} variant="caption">
                            {description}
                        </Typography>
                    )}
                </div>

                <div className={classes.column}>
                    {section && (
                        <div className={classes.primary}>
                            <Typography style={styles.subTitle} variant="h6">
                                {section}
                            </Typography>
                        </div>
                    )}
                    {subsection && (
                        <Typography style={styles.description} variant="caption">
                            {subsection}
                        </Typography>
                    )}
                </div>

                <div className={classes.spacer}>
                    {showDelete && (
                        <>
                            {disableDelete ? (
                                <Icon
                                    color={'blueFour'}
                                    data-testid="icon-trash-striped"
                                    name="trash-striped"
                                    size={24}
                                />
                            ) : (
                                <Icon
                                    color={'blueOne'}
                                    data-testid="icon-trash-striped"
                                    disabled={disableDelete}
                                    name="trash-striped"
                                    onPress={handleDeleteIconPress}
                                    size={24}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            <ConfirmModal
                cancelLabel="Cancel"
                confirmLabel="Delete"
                description="Are you sure you want to delete this fail code?"
                destructive
                dialogClassName={classes.confirmDeleteModal}
                note="Please note: a fail code is required to close the case."
                onCancel={handleDeleteCancelButtonClick}
                onClose={handleDeleteCancelButtonClick}
                onConfirm={handleDeleteConfirmButtonClick}
                open={showConfirmDelete}
                size="md"
                title="Delete Fail Code"
            />
        </div>
    );
}
